import React, { useEffect } from 'react';
import { Mic, X } from 'lucide-react';

/**
 * Voice Control Listening Overlay
 * 
 * Features:
 * - Fullscreen overlay
 * - Large "Listening" indicator
 * - Pulsing animation
 * - Transcript display
 * - Error messages
 * - Cancel button
 * 
 * Props:
 * - isListening: Boolean indicating if voice recognition is active
 * - transcript: Current transcript text
 * - error: Error message to display
 * - onCancel: Function to call when user cancels
 */
export default function VoiceOverlay({ isListening, transcript, error, onCancel }) {
  // Prevent scroll when overlay is open
  useEffect(() => {
    if (isListening) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isListening]);

  if (!isListening && !error) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-8">
      <div className="text-center max-w-2xl w-full">
        {/* Cancel Button */}
        <button
          onClick={onCancel}
          className="absolute top-8 right-8 w-16 h-16 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-all"
          aria-label="Abbrechen"
        >
          <X className="w-10 h-10 text-white" strokeWidth={2.5} />
        </button>

        {/* Error State */}
        {error && (
          <div className="bg-red-500/20 border-4 border-red-500 rounded-3xl p-12 mb-8">
            <div className="text-red-100 text-3xl md:text-4xl font-bold mb-4">
              ⚠️ Fehler
            </div>
            <div className="text-white text-2xl md:text-3xl">
              {error}
            </div>
          </div>
        )}

        {/* Listening State */}
        {isListening && !error && (
          <>
            {/* Pulsing Mic Icon */}
            <div className="relative w-48 h-48 mx-auto mb-12">
              {/* Outer pulse rings */}
              <div className="absolute inset-0 bg-blue-500/30 rounded-full animate-ping" />
              <div className="absolute inset-4 bg-blue-500/40 rounded-full animate-pulse" />
              
              {/* Inner mic icon */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-32 h-32 bg-blue-600 rounded-full flex items-center justify-center shadow-2xl">
                  <Mic className="w-20 h-20 text-white" strokeWidth={2.5} />
                </div>
              </div>
            </div>

            {/* Listening Text */}
            <h2 className="text-white text-5xl md:text-6xl font-bold mb-8 animate-pulse">
              Ich höre zu...
            </h2>

            {/* Transcript */}
            {transcript && (
              <div className="bg-white/20 rounded-3xl p-8 mb-8">
                <div className="text-white/70 text-xl mb-2">Sie haben gesagt:</div>
                <div className="text-white text-3xl md:text-4xl font-bold">
                  "{transcript}"
                </div>
              </div>
            )}

            {/* Hint Text */}
            <div className="text-white/80 text-2xl md:text-3xl space-y-3">
              <p>Sagen Sie einen Befehl wie:</p>
              <div className="grid grid-cols-2 gap-4 mt-6 text-xl">
                <div className="bg-white/10 rounded-2xl p-4">"Wetter"</div>
                <div className="bg-white/10 rounded-2xl p-4">"Nachrichten"</div>
                <div className="bg-white/10 rounded-2xl p-4">"Familie anrufen"</div>
                <div className="bg-white/10 rounded-2xl p-4">"Notfall"</div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
